import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Archive, Box } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import { CrateForm } from "@/components/forms/CrateForms";
import BulkDeleteButton from "@/components/BulkDeleteButton";
import { SearchFilters } from "@/components/SearchFilters";
import { Pagination } from "@/components/Pagination";
import { TableSkeleton } from "@/components/Skeleton";
import { CRATE_STATUSES } from "@/lib/constants";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PAGE_SIZE = 25;

export default function CratesListPage() {
  const { user } = useAuth();
  const isStaff = user?.role !== "client_user";
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, all_statuses: [] });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  const page = Math.max(1, parseInt(params.get("page") || "1"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {
        q: params.get("q") || undefined,
        status: params.get("status") || undefined,
        client: params.get("client") || undefined,
        include_archived: params.get("archived") === "1" || undefined,
        page, page_size: PAGE_SIZE,
      };
      const [c, cl] = await Promise.all([
        api.get("/crates", { params: q }),
        isStaff ? api.get("/clients") : Promise.resolve({ data: [] }),
      ]);
      setData(c.data); setClients((cl.data || []).filter((x) => !x.is_archived));
      setSelectedIds([]);
    } finally { setLoading(false); }
  }, [params, page, isStaff]);

  useEffect(() => { load(); }, [load]);

  const toggleOne = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allIds = data.items.map((c) => c.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : allIds);

  const allStatuses = data.all_statuses || [];
  const chip = (label, statuses) => ({ label, count: allStatuses.filter((s) => statuses.includes(s)).length });
  const chips = [
    chip("Empty", ["Empty in Warehouse"]),
    chip("With DSK", ["In Warehouse with DSK"]),
    chip("Packed", ["Packed with Product"]),
    chip("In Transit", ["In Transit to Store", "In Transit Back to Warehouse"]),
    chip("At Store / Waiting", ["Delivered to Store", "Waiting for Return"]),
    chip("Returned", ["Returned to Warehouse"]),
    chip("Damaged / Missing", ["Damaged", "Missing"]),
  ];

  const colCount = (isAdmin ? 1 : 0) + (isStaff ? 7 : 6);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Wooden Crates</h2>
          <p className="text-muted-foreground">{data.total} crate{data.total === 1 ? "" : "s"}</p>
        </div>
        {isStaff && clients.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && selectedIds.length > 0 && (
              <BulkDeleteButton
                selectedIds={selectedIds}
                resource="wooden crates"
                endpoint="/crates/bulk-delete"
                testIdRoot="crates"
                onDeleted={load}
              />
            )}
            {isAdmin && (
              <Button asChild size="sm" variant="outline" data-testid="archived-crates-link">
                <Link to="/crates/archived">
                  <Archive className="mr-1 h-3.5 w-3.5" /> Archived Crates
                </Link>
              </Button>
            )}
            <CrateForm clients={clients} onSaved={load} />
          </div>
        )}
      </div>
      {isStaff && clients.length === 0 && !loading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          To get started, add a client on the{" "}
          <Link to="/clients" className="font-semibold underline hover:text-amber-950">Clients page</Link>
          {" "}— then you can add users under that client and start tracking crates here.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1 text-sm">
            {c.label} <span className="font-semibold">{c.count}</span>
          </span>
        ))}
      </div>
      <SearchFilters statuses={CRATE_STATUSES} clients={isStaff ? clients : null} showArchivedToggle={isStaff} placeholder="Search crate ID or tracking..." />
      <Card>
        <CardContent className="p-0">
          {loading ? (<TableSkeleton rows={6} cols={colCount} />) :
            data.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      {isAdmin && (
                        <th className="w-10 px-4 py-3">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Select all crates on this page"
                            data-testid="crates-select-all"
                          />
                        </th>
                      )}
                      <th className="px-4 py-3 font-medium">Crate ID</th>
                      {isStaff && <th className="px-4 py-3 font-medium">Organization</th>}
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Location</th>
                      <th className="px-4 py-3 font-medium">Product Inside</th>
                      <th className="px-4 py-3 font-medium">Trips</th>
                      <th className="px-4 py-3 font-medium">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((c) => (
                      <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50">
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <Checkbox
                              checked={selectedIds.includes(c.id)}
                              onCheckedChange={() => toggleOne(c.id)}
                              aria-label={`Select crate ${c.crate_code}`}
                              data-testid={`crates-row-select-${c.id}`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3"><Link to={`/crates/${c.id}`} className="font-medium text-primary hover:underline">{c.crate_code}</Link></td>
                        {isStaff && <td className="px-4 py-3">{c.client_name || "—"}</td>}
                        <td className="px-4 py-3">
                          <StatusBadge status={c.status} />
                          {c.is_archived && <span className="ml-1 inline-flex items-center rounded-md bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">Archived</span>}
                        </td>
                        <td className="px-4 py-3">{c.current_location}</td>
                        <td className="px-4 py-3">{c.product ? c.product.serial_number : "—"}</td>
                        <td className="px-4 py-3">{c.reuse_count}</td>
                        <td className="px-4 py-3 text-muted-foreground">{new Date(c.updated_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
                <Box className="h-5 w-5" />
                <p>No crates match. Adjust filters or add a crate box.</p>
              </div>
            )
          }
        </CardContent>
      </Card>
      <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} />
    </div>
  );
}
